/**
 * studentAssessmentsAdapter — « Mes contrôles » de l'élève : les lignes servies par
 * `GET school-assessments?student_id=…&upcoming=1` (`SchoolAssessmentResource`) vers ce que la page
 * et sa feuille d'ajout lisent, et le chemin inverse (le corps de `POST` / `PUT`).
 *
 * La ligne d'un contrôle est celle du parent (`toUISchoolAssessment`, importée, pas déplacée), à un
 * écart près : sans chapitre lié, le parent fabrique une ligne de chapitre au nom du contrôle pour
 * que sa moyenne égale `preparation_progress` ; l'élève, lui, a une feuille de détail qui liste les
 * chapitres, et l'ancienne carte n'en montrait aucun dans ce cas. La moyenne d'une liste vide vaut 0,
 * comme `preparation_progress` sans chapitre : la ligne garde le même pourcentage.
 *
 * Le reste est le port littéral de l'ancien code :
 * - le brouillon d'une modification = `getSchoolAssessmentFormData(previousAssessment)`
 *   (`school-assessment/SchoolAssessmentTopicForm.js`) ;
 * - les matières proposées = `filteredSubjects` du même fichier (celles des objectifs en cours) ;
 * - les chapitres de la matière = `availableChapters` (le catalogue filtré sur la matière) ;
 * - le corps envoyé = `onSubmit` d'`AddSchoolAssessmentModal.js`.
 */
import moment from "moment"

import {
    toUISchoolAssessment,
    type ApiSchoolAssessment,
} from "../../parent/SchoolAssessments/schoolAssessmentListAdapter"
import { toUISectionedChapters, type ApiCatalogChapter } from "../../parent/SchoolAssessments/sectionedChapterAdapter"
import { toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import {
    type UICreateSchoolAssessment,
    type UISchoolAssessment,
    type UISectionedChapter,
    type UISubject,
} from "../../../ui/types"

/** Une ligne de la liste : celle du parent, plus `school_subject_id` et les chapitres liés. */
export type ApiStudentAssessment = ApiSchoolAssessment & {
    school_subject_id?: string | number | null
    chapters?: ApiCatalogChapter[] | null
}

/** Un chapitre du catalogue (`GET chapters/{matière}`) : `ChapterResource` porte sa matière. */
export type ApiStudentCatalogChapter = ApiCatalogChapter & { school_subject_id?: string | number | null }

/** Un objectif pédagogique du compte (`PedagogicalObjectiveResource`). */
export type ApiPedagogicalObjective = { status?: string | null; subject_ids?: (string | number)[] | null }

/** Ce qu'il faut pour rouvrir un contrôle dans la feuille : son brouillon, et ses propres chapitres. */
export type StudentAssessmentEdit = {
    draft: UICreateSchoolAssessment
    /** Les chapitres déjà liés : la liste du formulaire les connaît même s'ils sortent du catalogue. */
    chapters: UISectionedChapter[]
}

/**
 * Le jour `YYYY-MM-DD` d'une date servie, lu à l'heure de l'élève comme le faisait l'ancien formulaire :
 * le serveur écrit le minuit de Paris en UTC (`2026-10-21T22:00:00.000000Z` pour le 22/10).
 */
function calendarDay(date: string): string {
    const day = moment(date)
    return day.isValid() ? day.format("YYYY-MM-DD") : date
}

/** Une ligne servie → la ligne de la page. */
export function toStudentAssessment(raw: ApiStudentAssessment | null | undefined): UISchoolAssessment | null {
    const base = toUISchoolAssessment(raw)
    if (base == null) return null
    // Pas de chapitre lié : pas de ligne de chapitre inventée (voir l'en-tête).
    return (raw?.chapters_progress ?? []).length === 0 ? { ...base, chaptersProgress: [] } : base
}

/** La liste servie, dans son ordre (par date croissante) ; une ligne inutilisable tombe. */
export function toStudentAssessments(rows: ApiStudentAssessment[] | null | undefined): UISchoolAssessment[] {
    return (rows ?? []).map((row) => toStudentAssessment(row)).filter((row): row is UISchoolAssessment => row != null)
}

/**
 * Le brouillon d'une modification — port de `getSchoolAssessmentFormData(previousAssessment)` : la
 * date, `school_subject_id`, les ids de `chapters` (la relation, pas `chapters_progress`), « À
 * réviser », aucun chapitre libre.
 */
export function toStudentAssessmentEdit(raw: ApiStudentAssessment | null | undefined): StudentAssessmentEdit | null {
    if (raw?.id == null || !raw.date) return null
    const chapters = toUISectionedChapters(raw.chapters)
    return {
        draft: {
            subjectId: raw.school_subject_id ?? raw.school_subject?.id ?? null,
            date: calendarDay(raw.date),
            chapterIds: chapters.map((chapter) => chapter.id),
            customChapters: [],
            keyRequirementsText: raw.key_requirements ?? "",
            aiPreview: null,
        },
        chapters,
    }
}

/** Les brouillons de modification, par id de contrôle. */
export function toStudentAssessmentEdits(
    rows: ApiStudentAssessment[] | null | undefined
): Record<string, StudentAssessmentEdit> {
    const out: Record<string, StudentAssessmentEdit> = {}
    for (const row of rows ?? []) {
        const edit = toStudentAssessmentEdit(row)
        if (edit != null) out[String(row.id)] = edit
    }
    return out
}

/**
 * Les matières du formulaire — port de `filteredSubjects` : celles des objectifs pédagogiques qui ne
 * sont pas clos ; aucune (pas d'objectif, ou des objectifs sans matière) : toutes.
 */
export function subjectsForAssessment(
    subjects: ApiSchoolSubject[] | null | undefined,
    objectives: ApiPedagogicalObjective[] | null | undefined
): UISubject[] {
    const all = subjects ?? []
    const fromObjectives = new Set(
        (objectives ?? [])
            .filter((objective) => objective?.status !== "closed")
            .flatMap((objective) => objective?.subject_ids ?? [])
            .map((id) => String(id))
    )
    const kept = fromObjectives.size === 0 ? all : all.filter((subject) => fromObjectives.has(String(subject?.id)))
    return kept.map((subject) => toUISubject(subject))
}

/**
 * Les chapitres de la matière choisie — port de `availableChapters` : le catalogue en cache filtré
 * sur la matière (`chaptersList` est partagé : une autre surface peut l'avoir rempli pour une autre),
 * rien tant qu'aucune matière n'est choisie.
 */
export function chaptersForSubject(
    catalog: ApiStudentCatalogChapter[] | null | undefined,
    subjectId: string | number | null | undefined
): UISectionedChapter[] {
    if (subjectId == null) return []
    return toUISectionedChapters(
        (catalog ?? []).filter((chapter) => String(chapter?.school_subject_id) === String(subjectId))
    )
}

/**
 * Le catalogue plus les chapitres que le brouillon peut déjà porter (ceux lus par l'IA — le legacy
 * les ajoutait aussi —, ceux du contrôle qu'on modifie), sans doublon : la liste sait nommer chaque
 * id choisi.
 */
export function mergeChapters(catalog: UISectionedChapter[], ...extras: UISectionedChapter[][]): UISectionedChapter[] {
    const byId = new Map(catalog.map((chapter) => [String(chapter.id), chapter]))
    for (const chapter of extras.flat()) {
        if (!byId.has(String(chapter.id))) byId.set(String(chapter.id), chapter)
    }
    return [...byId.values()]
}

/** Le corps de `POST` / `PUT school-assessments` — celui d'`AddSchoolAssessmentModal.js`. */
export function toStudentAssessmentPayload(studentId: string | number, draft: UICreateSchoolAssessment) {
    return {
        student_id: studentId,
        school_subject_id: draft.subjectId,
        date: draft.date,
        chapter_ids: draft.chapterIds,
        custom_chapters: draft.customChapters.length > 0 ? draft.customChapters.map((c) => c.label) : undefined,
        key_requirements: draft.keyRequirementsText.trim() || undefined,
    }
}
